import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { memo, useMemo, useState } from "react";
import { getDataDistributions } from "../../api/dataSources";
import { ChartCard } from "../../components";
import DataCompletenessTable from "../../components/charts/DataCompletenessTable";
import StyledButton from "../../components/common/StyledButton";
import { defaultFilterState } from "../../constants/defaultFilterState";
import { useFilterContext } from "../../contexts/FilterProvider";
import { DataDistribution } from "../../models/datasource";
import { compactFormatter } from "../../utils/numberFormatter";
import { DataDistributionChart, DataRefreshTable } from "../charts";
import CommonTooltipContent from "../CommonTooltipContent";
import useUserContext from "../Authorization/useUserContext";

const T = Typography;

// Cache for 2 minutes
const TWO_MINUTES = 2 * 60 * 1000;

const DataDistributionCard = (): JSX.Element => {
  const user = useUserContext();
  const { filterState } = useFilterContext();

  const [expanded, setExpanded] = useState<boolean>(false);

  const {
    data: dataResponse,
    isLoading,
    isError,
  } = useQuery<DataDistribution[]>({
    queryKey: ["data_distribution", JSON.stringify(filterState), user.selectedRoles],
    queryFn: () => getDataDistributions(filterState || defaultFilterState, user),
    enabled: !!filterState,
    staleTime: TWO_MINUTES,
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    keepPreviousData: true, // Show previous data while fetching new data
  });

  // Memoize data to prevent unnecessary recalculations
  const data = useMemo(() => {
    if (!isError && !!dataResponse && dataResponse.length > 0) {
      return dataResponse;
    }
    return [];
  }, [dataResponse, isError]);

  // Memoize total records calculation
  const totalRecords = useMemo(() => {
    return data.reduce((sum, d) => d.record_count + sum, 0);
  }, [data]);

  console.debug("DataDistribution:render", { isLoading, data });

  // Show loading skeleton only on initial load, not on refetch
  const showSkeleton = isLoading && !data.length;

  return (
    <ChartCard
      title={["Data Distribution ", ...(showSkeleton ? [] : [`${compactFormatter.format(totalRecords)} records`])]}
      tooltipContent={
        <CommonTooltipContent
          header="Data Distribution"
          desc={
            <>
              This widget surfaces the distribution of text records from the selected data sources, based on your
              applied filters and search. It displays the record count and percentage from each source. When this widget
              is expanded, the Data Freshness and Data Completeness are displayed.
              <br />
              <br />
              Data Freshness represents the last date on which the data was ingested by Oasis, as well as by Deepsense.
              Data freshness can be utilized to validate the insights are up-to-date.
              <br />
              <br />
              Data completeness can be used to determine if a particular field is valuable to filter or if using
              Semantic Search would return more robust results. If the data completeness for a data source is 0%, it is
              likely that field is never captured for that data source.
            </>
          }
        />
      }
      actions={<StyledButton onClick={() => setExpanded(!expanded)}>Show {expanded ? "Less" : "More"}</StyledButton>}
    >
      {!showSkeleton ? (
        data.length ? (
          <>
            <DataDistributionChart data={data} />
            <Collapse in={expanded}>
              <Box>
                <T fontWeight={600} sx={{ pb: "8px", pt: "36px" }}>
                  Data Freshness
                </T>
                <DataRefreshTable data={data} />
              </Box>

              <Box>
                <T fontWeight={600} sx={{ pb: "8px", pt: "36px" }}>
                  Data Completeness
                </T>
                <DataCompletenessTable data={data} />
              </Box>
            </Collapse>
          </>
        ) : (
          <Box display="flex" flexDirection="row" justifyContent="center">
            <T>No records to show.</T>
          </Box>
        )
      ) : (
        <Skeleton variant="rounded" height={25} />
      )}
    </ChartCard>
  );
};

export default memo(DataDistributionCard);
